import { ChangeDetectorRef, Component, inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { ThreadUtils } from "@danangelus/ngx-utils";
import { ConfirmationService, TreeNode } from 'primeng/api'
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { Application } from '../../../../../models/application.model'
import { AppManagerService } from '../../../../../core/services/app-manager.service'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { FloatLabelModule } from 'primeng/floatlabel'
import { InputTextModule } from 'primeng/inputtext'
import { MessageModule } from 'primeng/message'
import { PaginatorModule } from 'primeng/paginator'
import { ValidationUtils } from '../../../../../core/utils/validation.utils'
import { KeyFilterModule } from 'primeng/keyfilter'
import { FileUpload, FileUploadModule } from 'primeng/fileupload'
import { LoggerService } from '@danangelus/ngx-app-core'
import { PickListModule } from 'primeng/picklist'
import { Group } from '../../../../../models/group.model'
import { LdapManagerService } from '../../../../../core/services/ldap-manager.service'
import { DividerModule } from 'primeng/divider'
import { NgClass } from '@angular/common'
import { TreeModule } from 'primeng/tree'
import { TreeTableModule } from 'primeng/treetable'
import { Page } from '../../../../../models/page.model'
import { PermissionGroup } from '../../../../../models/permission-group.model'
import { GatewayError } from '../../../../../models/gateway.model'
import { SamlServiceProvider } from "../../../../../models/saml-provider.model"
import { Textarea } from "primeng/textarea"
import { Select } from "primeng/select"

const log = LoggerService.get('EditApplicationComponent')

/**
 * Component responsible for the management of an application
 * <ul>
 *     <li>Creating</li>
 *     <li>Modifying</li>
 *     <li>Deleting</li>
 * </ul>
 *
 * @author Connor Wheatley
 */
@Component({
    selector: 'edit-application',
    standalone: true,
    imports: [
        // Angular
        NgClass,
        ReactiveFormsModule,
        // PrimeNG
        ConfirmDialogModule,
        DividerModule,
        FileUploadModule,
        FloatLabelModule,
        ToggleSwitchModule,
        InputTextModule,
        KeyFilterModule,
        MessageModule,
        PaginatorModule,
        PickListModule,
        TreeModule,
        TreeTableModule,
        Textarea,
        Select,
    ],
    providers: [
        ConfirmationService
    ],
    templateUrl: './edit-application.component.html',
    styleUrl: './edit-application.component.sass',
    encapsulation: ViewEncapsulation.None
})
export class EditApplicationComponent implements OnInit {

    router = inject(Router)
    route = inject(ActivatedRoute)
    changeDetector = inject(ChangeDetectorRef)
    confirmationService = inject(ConfirmationService)
    appManagerService = inject(AppManagerService)
    ldapService: LdapManagerService = inject(LdapManagerService)

    // @ts-expect-error Assigned in ngOnInit
    form: UntypedFormGroup

    messageType = ''
    editing = false

    id = ''
    currentImage: string | undefined
    application: Application | undefined

    groups: Group[] = []
    availableGroups: Group[] = []
    assignedGroups: Group[] = []

    pageTree: TreeNode[] = []

    signatureAlgorithms = [
        "http://www.w3.org/2000/09/xmldsig#dsa-sha1",
        "http://www.w3.org/2000/09/xmldsig#rsa-sha1",
        "http://www.w3.org/2001/04/xmldsig-more#rsa-sha256",
        "http://www.w3.org/2001/04/xmldsig-more#rsa-sha384",
        "http://www.w3.org/2001/04/xmldsig-more#rsa-sha512"
    ]

    // Used to edit the icon upload so that it can be reset to be used more than once
    @ViewChild('fileUpload') fileUploader!: FileUpload

    async ngOnInit() {
        this.route.params.subscribe(params => {
            this.id = params['applicationId']
            this.init().then(() => log.debug('ngOnInit', `Application [${this.application?.id}] has been loaded`))
        })
    }

    private async init() {
        if (this.id) {
            await ThreadUtils.sleep(200)
            this.application = await this.appManagerService.loadApplication(this.id!)
            this.editing = true
            if (this.currentImage) {
                this.currentImage = this.application?.icon
            }
        } else {
            this.application = new Application()
        }

        this.groups = (await this.ldapService.getAllGroups()) || []

        this.groups.forEach(group => {
            const isAssigned = this.application?.groups.some(
                (permissionGroup => permissionGroup.name === group.groupId)
            )
            if (isAssigned) {
                this.assignedGroups.push(group)
            } else {
                this.availableGroups.push(group)
            }
        })
        this.sortGroups(this.availableGroups)
        this.sortGroups(this.assignedGroups)

        this.buildPageTree()

        const form = new UntypedFormGroup({
            baseUrl: new UntypedFormControl(this.application?.baseUrl, Validators.required),
            description: new UntypedFormControl(this.application?.description, []),
            displayOrder: new UntypedFormControl(this.application?.displayOrder, Validators.required),
            expertCode: new UntypedFormControl(this.application?.expertCode, Validators.required),
            icon: new UntypedFormControl(this.application?.icon, []),
            locked: new UntypedFormControl(this.application?.locked, []),
            name: new UntypedFormControl(this.application?.name, Validators.required),
            title: new UntypedFormControl(this.application?.title, []),
            groups: new UntypedFormControl(this.application?.groups, []),
            useSaml: new UntypedFormControl(this.application?.useSaml, []),
            samlAcsUrl: new UntypedFormControl(this.application?.samlServiceProvider?.acsUrl, []),
            samlAcsValidatorPattern: new UntypedFormControl(this.application?.samlServiceProvider?.acsValidatorPattern, []),
            samlSignatureAlgorithm: new UntypedFormControl(this.application?.samlServiceProvider?.signatureAlgorithm, []),
            samlSignatureKey: new UntypedFormControl(this.application?.samlServiceProvider?.signatureKey, []),
            samlSignaturePassword: new UntypedFormControl(this.application?.samlServiceProvider?.signaturePassword, []),
            samlEncryptionCertificate: new UntypedFormControl(this.application?.samlServiceProvider?.encryptionCertificate, []),
            samlProviderEnabled: new UntypedFormControl(this.application?.samlServiceProvider?.enabled, [])
        })

        this.form = form
    }

    /**
     * A handler for the save button's click event.
     *
     * This will send a PUT request via {@linkcode AppManagerService#saveApplication} when saving an existing Application
     * Or it will send a POST request via {@linkcode AppManagerService#createApplication} when creating a new Application
     */
    async save(): Promise<void> {
        await this.validateAll()
        if (this.form.invalid) {
            this.messageType = 'FORM_INVALID'
            return
        }

        try {
            const {
                samlAcsUrl,
                samlAcsValidatorPattern,
                samlEncryptionCertificate,
                samlProviderEnabled,
                samlSignatureAlgorithm,
                samlSignatureKey,
                samlSignaturePassword,
                ...applicationData
            } = this.form.value
            const samlProvider = new SamlServiceProvider({
                acsUrl: samlAcsUrl,
                acsValidatorPattern: samlAcsValidatorPattern,
                encryptionCertificate: samlEncryptionCertificate,
                entityId: applicationData.baseUrl,
                providerEnabled: samlProviderEnabled,
                signatureAlgorithm: samlSignatureAlgorithm,
                signatureKey: samlSignatureKey,
                signaturePassword: samlSignaturePassword
            })
            this.application = new Application({
                ...applicationData, samlServiceProvider: samlProvider
            })
            this.application.groups = this.assignedGroups.map(group => new PermissionGroup(group.groupId, group.dn))

            let response: any
            if (this.id) {
                this.application!.id = this.id
                response = await this.appManagerService.saveApplication(this.application!)
            } else {
                response = await this.appManagerService.createApplication(this.application!)
            }

            if (response === true) {
                log.debug('save', `Application [${this.application!.id}] has been updated`)
                this.messageType = 'CHANGES_SAVED'
                await this.cancel()
            } else if (response instanceof GatewayError) {
                switch (response.error) {
                    case 'NOT_UNIQUE':
                        this.messageType = response.error
                        this.form.controls['expertCode'].setErrors({ duplicate: true})
                        break
                    default:
                        this.messageType = 'FORM_INVALID'
                }
            }
        } catch (err: any) {
            log.error(`Error when ${this.editing ? 'updating' : 'creating'} Application ${this.application!.id} ${err}`)
        }
    }

    /**
     * A handler for the delete button's click event.
     *
     * This will send a delete request via {@linkcode AppManagerService#deleteApplication}
     * to remove the current Application from the database
     */
    async delete(event: Event): Promise<void> {
        log.info('delete', `Attempting to delete application.`)
        event.preventDefault()
        this.confirmationService.confirm({
            message: `Are you sure you want to delete ${this.application?.name}`,
            header: `Delete ${this.application?.name}?`,
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                log.debug('delete', `Deleting Application ${this.application?.name}`)
                const response = await this.appManagerService.deleteApplication(this.application!.id!)
                if (response instanceof GatewayError) {
                    switch (response.error) {
                        case 'NOT_EXISTS':
                        case 'IN_USE':
                            this.messageType = response.error
                            break
                        default:
                            this.messageType = 'ERROR_SAVING'
                    }
                } else {
                    await this.cancel()
                }
            },
            reject: () => {
                this.messageType = 'DELETION_CANCELLED'
            }
        })
    }

    /**
     * Handler for the cancel button's click event.
     *
     * This will cancel the create/update and redirect the user back to the list view
     */
    async cancel(): Promise<void> {
        await this.router.navigateByUrl('/administration/application', { replaceUrl: true })
    }

    /**
     * Mark each FormControl as dirty and re-evaluate validity to establish
     * validation failures
     */
    async validateAll(): Promise<void> {
        Object.values(this.form.controls).forEach(control => {
            control.markAsDirty()
        })
        this.form.updateValueAndValidity()
    }

    /**
     * Handler for the file upload function to store the image as a Base64 string to be stored against the icon field
     *
     * @param event
     */
    async onUpload(event: any): Promise<void> {
        log.info('onUpload', `Uploading application icon.`)
        const file = event.files[0]
        if (file) {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = async () => {
                this.currentImage = reader.result?.toString()
                this.fileUploader.clear()
                this.form.get('icon')?.setValue(this.currentImage)
            }
        }
    }

    /**
     * Sort the assigned/available groups in the picklist in alphabetical order
     *
     * @param groups List of assigned/available groups
     */
    sortGroups(groups: Group[]): void {
        groups.sort((group1, group2) => {
            // Use displayName if available, otherwise use groupId, or fallback to empty string
            const sortOn1 = group1.displayName ?? group1.groupId ?? ''
            const sortOn2 = group2.displayName ?? group2.groupId ?? ''

            return sortOn1.localeCompare(sortOn2)
        })
    }

    private createPageNode(page: Page): TreeNode {
        return {
            label: `${page.name} [${page.displayOrder}]`,
            data: {
                id: `${page.id}`,
                displayOrder: `${page.displayOrder}`,
                name: `${page.name}`,
                title: `${page.title || ''}`,
                expertCode: `${page.expertCode}`,
                locked: page.locked,
            },
            key: page.parentPageId ? page.parentPageId.toString() : undefined,
            children: []
        }
    }

    private buildTree(pageMap: Map<string, TreeNode>, parentBranch: TreeNode[], parentId: string | undefined): void {
        this.application?.pages
            .filter((page: Page) => (!parentId && !page.parentPageId) || (page.parentPageId === parentId))
            .forEach((page: Page) => {
                const pageNode = this.createPageNode(page)
                pageNode.children = []

                parentBranch.push(pageNode)

                pageMap.set(page.id!, pageNode)
                this.buildTree(pageMap, pageNode.children, page.id)
            })
    }

    /**
     * Recursively builds a tree structure from the list of pages
     */
    buildPageTree(): void {
        const pageMap = new Map<string, TreeNode>()
        const fullPageTree: TreeNode[] = []
        this.buildTree(pageMap, fullPageTree, undefined)

        // updating this.pageTree directly does not work with TreeTable, so it has to be done this way
        this.pageTree = fullPageTree
    }

    async addPage(parentPageId?: string) {
        log.debug('addPage', 'Creating a new page')
        await this.router.navigateByUrl(`/administration/application/${this.application!.id}/page/create?parentPageId=${parentPageId || ''}`)
    }

    async editPage(id: string): Promise<void> {
        log.debug('editPage', `Editing a page with id: [${id}]`)
        await this.router.navigateByUrl(`/administration/application/${this.application!.id}/page/${id}`)
    }

    protected readonly ValidationUtils = ValidationUtils

    clearMessages() {
        this.messageType = ''
    }
}
